<template>
  <Permissioned
    section="applications"
    vertical
    :id="application.id"
    v-resize="onResize"
  >
    <LabeledText v-if="application.rejectionReason" label="Rejection Reason">
      {{ application.rejectionReason }}
    </LabeledText>
    <Notes
      :notes="comments"
      :isAdding="isAddingComment"
      :isLoading="isLoadingComments"
      :files="files"
      @unselectFile="unselectFile"
      @selectFile="selectFile"
      @note="createNewNote"
      withAttachments
      ref="notes"
    />
    <Permissioned section="workers" vertical v-if="showAddress">
      <LabeledText permission="view.address" label="Address">
        {{ workerAddress || "-" }}
      </LabeledText>
    </Permissioned>
    <LabeledText label="Source">
      {{ utmSource || "-" }}
    </LabeledText>
    <Permissioned section="workers" vertical>
      <BooleanChip
        label="Internal Candidate"
        permission="view.internal-candidate"
        :value="isInternalCandidate"
      />
    </Permissioned>
    <BooleanChip
      label="Redeployment Candidate"
      :value="isRedeploymentCandidate"
    />
    <BooleanChip
      label="Met Mandatory Requirements"
      :value="isMeetsRequirements"
      v-if="isMeetsRequirements"
    />
    <BaseDetails permission="view.internal-reference-number" class="mb-4">
      <template v-slot:header>
        <div class="reference-number">
          <h3>Internal Reference Number</h3>
          <Permissioned section="applications" :id="application.id">
            <PrimaryButton
              permission="edit.internal-reference-number"
              light
              dense
              shrink
              @click.native="editReferenceNumber = true"
            >
              Edit
              <template v-slot:append:icon>
                <v-icon>mdi-pencil</v-icon>
              </template>
            </PrimaryButton>
          </Permissioned>
        </div>
        <EditInternalReferenceNumber
          :key="application.id"
          :application="application"
          :isOpen="editReferenceNumber"
          @close="editReferenceNumber = false"
        />
      </template>
      <div :class="{ 'mt-1': isMobile }">
        {{ application.applicationReference || "-" }}
      </div>
    </BaseDetails>
    <v-divider />
    <ExperienceList
      v-if="worker.id"
      :userId="worker.id"
      :bookingId="booking.id"
      :permission="`view.${permissions.EXPERIENCE.experience}`"
      showDivider
    >
      <template v-slot:header>
        <h3 class="mb-5">Experience</h3>
      </template>
    </ExperienceList>
    <QualificationList
      v-if="worker.id"
      :userId="worker.id"
      :bookingId="booking.id"
      showDivider
      :permission="`view.${permissions.QUALIFICATIONS.qualifications}`"
    >
      <template v-slot:header>
        <h3 class="mb-5">Qualifications</h3>
      </template>
    </QualificationList>
    <Permissioned section="workers.view" vertical>
      <LanguageList
        v-if="worker.id"
        :userId="worker.id"
        :permission="permissions.LANGUAGES_AND_SKILLS.languages"
        showDivider
      >
        <template v-slot:header>
          <h3 class="mb-5">Languages</h3>
        </template>
      </LanguageList>
      <SkillList
        v-if="worker.id"
        :permission="permissions.LANGUAGES_AND_SKILLS.skills"
        :userId="worker.id"
        showDivider
      >
        <template v-slot:header>
          <h3 class="mb-5">Skills</h3>
        </template>
      </SkillList>
    </Permissioned>
    <h3 class="mb-3">Application Questions</h3>
    <ApplicationQuestionsWithAnswers
      v-if="booking.id"
      :applicationId="application.id"
      :bookingId="booking.id"
    />
    <OtherApplications
      v-if="worker.id && booking.id && !booking.anonymiseApplications"
      :userId="worker.id"
      permission="view.other-applications"
    />
    <BaseDetails
      class="mb-4"
      title="Assessment"
      permission="view.assessment"
      v-if="
        currentApplication &&
          currentApplication.assessments &&
          currentApplication.assessments.length
      "
    >
      <AssessmentItem
        v-for="assessment in currentApplication.assessments"
        :assessment="assessment"
        :key="assessment.id"
      />
    </BaseDetails>
    <v-divider v-if="worker.cv" />
    <CVPreview
      class="mb-4"
      permission="view.cv"
      v-if="worker && worker.cv"
      :url="worker.cv.url"
      :type="worker.cv.mimeType"
    />
  </Permissioned>
</template>

<script>
import BaseDetails from "@/components/common/BaseDetails/BaseDetails";
import CVPreview from "@/views/bookings/JobCandidate/components/CVPreview";
import AssessmentItem from "@/views/bookings/Assessment/AssessmentItem";
import EditInternalReferenceNumber from "@/views/bookings/components/ConfirmDialogs/EditInternalReferenceNumber";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import Permissioned from "@/components/common/Permissioned";
import Notes from "@/components/common/Notes/Notes";
import ExperienceList from "@/components/workers/experience/ExperienceList";
import QualificationList from "@/components/workers/qualifications/QualificationList";
import OtherApplications from "@/views/bookings/BookingDetails/CandidateDetails/components/OtherApplications";
import ApplicationQuestionsWithAnswers from "@/components/workers/ApplicationQuestionsWithAnswers";
import { createNamespacedHelpers } from "vuex";
import { APPLICATION_NAMESPACE } from "@/store/modules/applications";
import {
  GET_APPLICATION_COMMENTS,
  GET_CURRENT_APPLICATION,
  GET_IS_FETCHING_CURRENT_APPLICATION,
  GET_IS_LOADING_APPLICATION_COMMENTS
} from "@/store/modules/applications/getters";
import {
  FETCH_APPLICATION_COMMENTS,
  ADD_APPLICATION_COMMENT,
  FETCH_APPLICATION_ON_BOOKING
} from "@/store/modules/applications/actions";
import { remove, startCase, map } from "lodash";
import LanguageList from "@/components/workers/languages/LanguageList";
import SkillList from "@/components/workers/skills/SkillList";
import { formatAddress } from "@/utils/users";
import LabeledText from "@/components/common/BaseDetails/LabeledText";
import {
  FETCH_COUNTRIES,
  ADD_COMMENT_FILE
} from "@/store/modules/data/actions";
import { DATA_NAMESPACE } from "@/store/modules/data";
import { isEmpty } from "lodash";
import BooleanChip from "@/components/common/BaseDetails/BooleanChip";
import { PERMISSIONS } from "@/constants/workers";
import $ from "jquery";

const {
  mapActions: mapApplicationActions,
  mapGetters: mapApplicationGetters
} = createNamespacedHelpers(APPLICATION_NAMESPACE);
const {
  mapState: dataMapState,
  mapActions: mapDataActions
} = createNamespacedHelpers(DATA_NAMESPACE);

export default {
  name: "CandidateDetails",
  props: {
    comparingMode: Boolean,
    application: Object
  },
  async created() {
    this.fetchApplicationComments(this.application.id);
    await this.fetchApplication(this.application.id);
    this.workerAddress = formatAddress(this.worker.addresses, this.countries);
    if (isEmpty(this.countries)) {
      await this.fetchCountries();
    }
  },
  mounted() {
    if (!this.isMobile) {
      this.adjustPageStyles();
    }
  },
  destroyed() {
    this.restorePageStyles();
  },
  data() {
    return {
      editReferenceNumber: false,
      editAwrStatus: false,
      files: [],
      workerAddress: null,
      isAddingComment: false,
      permissions: PERMISSIONS
    };
  },
  computed: {
    ...mapApplicationGetters({
      comments: GET_APPLICATION_COMMENTS,
      isLoadingComments: GET_IS_LOADING_APPLICATION_COMMENTS,
      currentApplication: GET_CURRENT_APPLICATION,
      isFetchingCurrentApplication: GET_IS_FETCHING_CURRENT_APPLICATION
    }),
    ...dataMapState({
      countries: state => state.countries
    }),
    worker() {
      return !this.isFetchingCurrentApplication &&
        this.currentApplication &&
        this.currentApplication.user
        ? this.currentApplication.user
        : {};
    },
    booking() {
      return !this.isFetchingCurrentApplication &&
        this.currentApplication &&
        this.currentApplication.booking
        ? this.currentApplication.booking
        : {};
    },
    tel() {
      return `tel:${this.worker.phone}`;
    },
    mailto() {
      return `mailto:${this.worker.email}`;
    },
    utmSource() {
      return startCase(this.application.utm_source);
    },
    isInternalCandidate() {
      return this.worker.isInternalCandidate;
    },
    isRedeploymentCandidate() {
      return this.worker.isRedeploymentCandidate;
    },
    isMeetsRequirements() {
      return this.application.meetsRequirements;
    },
    showAddress() {
      return !isEmpty(this.worker.addresses);
    }
  },
  components: {
    OtherApplications,
    Permissioned,
    PrimaryButton,
    EditInternalReferenceNumber,
    AssessmentItem,
    CVPreview,
    BaseDetails,
    Notes,
    ExperienceList,
    QualificationList,
    ApplicationQuestionsWithAnswers,
    LanguageList,
    SkillList,
    LabeledText,
    BooleanChip
  },
  methods: {
    ...mapApplicationActions({
      addApplicationComment: ADD_APPLICATION_COMMENT,
      fetchApplicationComments: FETCH_APPLICATION_COMMENTS,
      fetchApplication: FETCH_APPLICATION_ON_BOOKING
    }),
    ...mapDataActions({
      fetchCountries: FETCH_COUNTRIES,
      addCommentFile: ADD_COMMENT_FILE
    }),
    onResize() {
      const isMobile = window.innerWidth < 600;
      if (isMobile) {
        this.restorePageStyles();
      } else {
        this.adjustPageStyles();
      }
    },
    getScrollableContentHeight() {
      const pageHeight = $(".v-application").height();
      const appBarHeight = $(".app-bar").height();
      const headerHeight = $("header").height();
      const subheaderHeight = $(".subheader").height();

      return pageHeight - appBarHeight - headerHeight - subheaderHeight;
    },
    restorePageStyles() {
      $(".candidate-list").css("height", "auto");
      $(".submit-worker-details").css("height", "auto");
      const $applicationElement = $(".v-application");
      $applicationElement.css("overflow", "auto");
    },
    adjustPageStyles() {
      const $applicationElement = $(".v-application");
      $applicationElement.css("overflow", "scroll");
      const scrollableContentHeight = this.getScrollableContentHeight();
      $(".candidate-list").css("height", scrollableContentHeight);
      $(".submit-worker-details").css("height", scrollableContentHeight);
    },
    async createNewNote(comment) {
      this.isAddingComment = true;
      const { data: newComment } = await this.addApplicationComment({
        comment: {
          body: comment,
          commentableType: "user_application",
          commentableId: this.application.id,
          notifyListeners: true
        },
        applicationId: this.application.id
      });
      if (newComment) {
        await this.uploadCommentFiles(newComment.id);
      }
      this.isAddingComment = false;
      await this.fetchApplicationComments(this.application.id);
    },
    async uploadCommentFiles(commentId) {
      const requests = map(this.files, file => {
        const formData = new FormData();
        formData.append("file", file.data);
        return this.addCommentFile({
          id: commentId,
          data: formData
        });
      });
      this.files = [];
      this.$refs.notes.resetFileInput();
      return Promise.all(requests);
    },
    selectFile(file) {
      this.files.push(file);
    },
    unselectFile(fileToBeRemoved) {
      remove(this.files, file => file.id === fileToBeRemoved.id);
    }
  }
};
</script>

<style lang="scss"></style>
